import { Box, Notification, Typography } from "@drone-js/core";
import { singleMultipleBinder } from "libs/analytics/binders/propertySelection/singleMultiple";
import { useAnalytics } from "libs/analytics/useAnalytics";
import type { FunctionComponent } from "react";
import { Footer } from "../Footer";
import { UnknownErrorToast } from "../propertyCategory/UnknownErrorToast";
import { ValidationError } from "../propertyCategory/constants";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { useKeyDownHandler } from "../useKeyDownHandler";
import { CardSelector } from "./CardSelector";
import { DifferentLocationModal } from "./DifferentLocationModal";
import { NotSelectedNotification } from "./NotSelectedNotification";
import { HowManyToList } from "./constants";
import { useHowManyToListValue } from "./howManyToListAtom";
import { useSingleMultipleHandler } from "./useSingleMultipleHandler";

export const SingleMultiplePage: FunctionComponent = () => {
    const {
        navigationCms: { next, back },
        singleMultipleCms: {
            howManyUnitsDoYouWantToList,
            oneUnit,
            youHaveOneRentableUnit,
            multipleUnits,
            youHaveMultipleRentableUnits,
            tip,
            pleaseEnsureYouProvideAccurateInformation,
        },
    } = usePropertySelectionViewModelValue();
    const { loading, error, onBack, onNext } = useSingleMultipleHandler();
    const selectedOption = useHowManyToListValue();

    useKeyDownHandler(
        (e) => {
            if (e.code === "Enter") onNext();
        },
        [onNext],
    );

    useAnalytics(singleMultipleBinder);

    return (
        <Box py="24" gap="24" display="flex" flexDirection="column" qM={{ py: "32" }}>
            <Typography variant="heading-xl" textColor="base.default">
                {howManyUnitsDoYouWantToList}
            </Typography>
            {error === ValidationError.NotSelected && selectedOption === undefined && <NotSelectedNotification />}
            <Box display="flex" flexDirection="column" gap="16">
                <CardSelector
                    title={oneUnit}
                    subtitle={youHaveOneRentableUnit}
                    value={HowManyToList.Single}
                    disabled={loading}
                    dataElementName="one-unit-property-card"
                />
                <CardSelector
                    title={multipleUnits}
                    subtitle={youHaveMultipleRentableUnits}
                    value={HowManyToList.Multiple}
                    disabled={loading}
                    dataElementName="multiple-units-property-card"
                />
                <DifferentLocationModal />
            </Box>
            <Box display="none" qM={{ display: "block" }}>
                <Notification
                    intent="notice"
                    title={tip}
                    description={pleaseEnsureYouProvideAccurateInformation}
                    asset={{
                        id: "fill.symbol.lightbulb",
                    }}
                />
            </Box>
            {error === ValidationError.Unknown && <UnknownErrorToast />}
            <Footer
                secondary={{
                    label: back,
                    disabled: loading,
                    dataElementName: "back",
                }}
                primary={{
                    label: next,
                    loading: loading,
                    disabled: loading,
                    dataElementName: "next",
                }}
                onSecondary={onBack}
                onPrimary={onNext}
            />
        </Box>
    );
};
